import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";

// Small style helpers to avoid nested ternaries in JSX (readability).
const USER_STATUS_STYLES = {
  approved: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
};
const userStatusClass = (s) => USER_STATUS_STYLES[s] || "bg-slate-50 text-slate-600";
import { toast } from "sonner";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAdminPending } from "@/context/AdminPendingContext";
import { Users, BookOpen, PlayCircle, Radio, CalendarClock, Check, X, Trash2, Upload, Plus, ShieldCheck, Pencil, Search, Eye, Clock, Lock, Unlock, MessageSquareWarning, Star, Send, MessageCircle, Megaphone, ChevronDown, Loader2, Image as ImageIcon } from "lucide-react";
import EbookCover, { EBOOK_COVER_FALLBACK } from "@/components/EbookCover";
import ContentStatsBar from "@/components/ContentStatsBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function EditMediaDialog({ open, onOpenChange, kind, initial, onSave }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initial) {
      setTitle(initial.title || "");
      setDescription(initial.description || "");
      setUrl(initial.url || "");
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title tidak boleh kosong."); return; }
    const payload = { title: title.trim(), description: description.trim() };
    if (kind === "video" && url.trim()) payload.url = url.trim();
    setSaving(true);
    try { await onSave(payload); onOpenChange(false); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={`edit-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Rename {kind}</DialogTitle>
          <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
            Kemaskini detail {kind}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Title</label>
            <input
              autoFocus
              data-testid={`edit-${kind}-title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff]"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Description</label>
            <textarea
              data-testid={`edit-${kind}-description`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] resize-none"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          {kind === "video" && (
            <div className="space-y-1.5">
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Drive / YouTube URL</label>
              <input
                data-testid="edit-video-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Leave blank to keep current"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff]"
                style={{ transition: "border-color 0.2s ease" }}
              />
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <button
              type="button"
              data-testid={`edit-${kind}-cancel`}
              onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-full text-xs font-mono-plex tracking-widest uppercase border border-slate-200 text-slate-600 hover:bg-slate-50"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid={`edit-${kind}-save`}
              className="px-5 py-2.5 rounded-full text-xs font-mono-plex tracking-widest uppercase bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onOpenChange, onSaved }) {
  const [tg, setTg] = useState("");
  const [lp, setLp] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setTg(user.telegram_username || "");
      setLp(user.loyal_primus_client_id || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!lp.trim()) { toast.error("Loyal Primus Client ID tidak boleh kosong."); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        telegram_username: tg.trim(),
        loyal_primus_client_id: lp.trim(),
      });
      toast.success("User details updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o && !saving) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit user</DialogTitle>
          <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
            {user?.email}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Telegram username</label>
            <input
              autoFocus
              data-testid="edit-user-telegram"
              value={tg}
              onChange={(e) => setTg(e.target.value)}
              placeholder="e.g. nanatradingsecret"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Loyal Primus Client ID</label>
            <input
              data-testid="edit-user-client-id"
              value={lp}
              onChange={(e) => setLp(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm font-mono-plex"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <p className="text-[10px] font-mono-plex text-slate-400">Password akan auto-sync bila Client ID berubah.</p>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="edit-user-save"
              disabled={saving}
              className="px-5 py-2.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [email, setEmail] = useState("");
  const [lp, setLp] = useState("");
  const [tg, setTg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setLp(""); setTg(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !lp.trim() || !tg.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", {
        email: email.trim(),
        loyal_primus_client_id: lp.trim(),
        telegram_username: tg.trim(),
      });
      toast.success("User created & auto-approved");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onOpenChange(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New user</DialogTitle>
          <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
            Admin add — auto-approved. Password = Loyal Primus Client ID.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Email</label>
            <input
              autoFocus
              data-testid="new-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Loyal Primus Client ID</label>
            <input
              data-testid="new-user-client-id"
              value={lp}
              onChange={(e) => setLp(e.target.value)}
              placeholder="e.g. LP123456"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm font-mono-plex"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Telegram username</label>
            <input
              data-testid="new-user-telegram"
              value={tg}
              onChange={(e) => setTg(e.target.value)}
              placeholder="e.g. nanatradingsecret"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-user-save"
              disabled={busy}
              className="px-5 py-2.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {busy ? "Creating…" : "Create user"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApproveUsers() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [journalUserId, setJournalUserId] = useState(null);
  const [journalOpen, setJournalOpen] = useState(false);
  const LIMIT = 20;

  // Debounce search input by 250ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (opts = {}) => {
    const targetPage = opts.page ?? page;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (debouncedSearch) params.set("q", debouncedSearch);
      params.set("page", String(targetPage));
      params.set("limit", String(LIMIT));
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      // Support legacy array response as fallback.
      if (Array.isArray(data)) {
        setUsers(data);
        setTotal(data.length);
        setPages(1);
        setPage(1);
      } else {
        setUsers(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
        setPage(data.page || 1);
      }
    } catch (err) { logDev(err); }
    finally { setLoading(false); }
  };
  // Reset to page 1 when filter or search changes.
  useEffect(() => { setPage(1); load({ page: 1 }); /* eslint-disable-next-line */ }, [filter, debouncedSearch]);
  useEffect(() => { load({ page }); /* eslint-disable-next-line */ }, [page]);

  const approve = async (id) => { await api.post(`/admin/users/${id}/approve`); toast.success("Approved"); load(); };
  const openReject = (u) => { setRejectTarget(u); };
  const toggleMaster = async (u) => {
    const next = !u.is_master;
    try {
      await api.post(`/admin/users/${u.id}/set-master`, { is_master: next });
      toast.success(next ? "User upgraded to Master Trader." : "Master status removed.");
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_master: next } : x)));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const openJournal = (id) => { setJournalUserId(id); setJournalOpen(true); };
  const submitReject = async () => {
    if (!rejectTarget) return;
    setRejectBusy(true);
    try {
      await api.post(`/admin/users/${rejectTarget.id}/reject`);
      toast.warning("Rejected & removed");
      setRejectTarget(null);
      load();
    } catch { toast.error("Reject failed"); }
    finally { setRejectBusy(false); }
  };

  const doBulkApprove = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-approve", { all_pending: true });
      toast.success(`Approved ${data.count || 0} pending user(s)`);
      setBulkApproveOpen(false);
      setPage(1); load({ page: 1 });
    } catch { toast.error("Bulk approve failed"); }
    finally { setBulkBusy(false); }
  };

  const doBulkReject = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-reject", { all_pending: true });
      toast.warning(`Rejected & removed ${data.count || 0} pending user(s)`);
      setBulkRejectOpen(false);
      setPage(1); load({ page: 1 });
    } catch { toast.error("Bulk reject failed"); }
    finally { setBulkBusy(false); }
  };

  const startIdx = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const endIdx = Math.min(page * LIMIT, total);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          {["pending", "approved", "all"].map((f) => (
            <button
              key={f}
              data-testid={`user-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
              style={{ transition: "background-color 0.2s ease" }}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="new-user-btn"
            onClick={() => setNewUserOpen(true)}
            className="inline-flex items-center gap-1.5 bg-[#00b4ff] hover:bg-[#0090cc] text-white px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase font-semibold"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <Plus size={12} /> New User
          </button>
          {filter === "pending" && total > 0 && (
            <>
              <button
                data-testid="bulk-approve-all"
                onClick={() => setBulkApproveOpen(true)}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase font-semibold"
                style={{ transition: "background-color 0.2s ease" }}
              >
                <Check size={12} /> Approve All ({total})
              </button>
              <button
                data-testid="bulk-reject-all"
                onClick={() => setBulkRejectOpen(true)}
                className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase font-semibold"
                style={{ transition: "background-color 0.2s ease" }}
              >
                <X size={12} /> Reject All ({total})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search bar — filters across pages via backend `q` param */}
      <div className="mb-4 relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          data-testid="user-search-input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, Telegram, or Loyal Primus Client ID…"
          className="w-full pl-11 pr-11 py-2.5 rounded-full border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm bg-white"
          style={{ transition: "border-color 0.2s ease" }}
        />
        {search && (
          <button
            type="button"
            data-testid="user-search-clear"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full grid place-items-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {users.length === 0 ? (
        <Empty>{loading ? "Loading…" : "No users in this filter."}</Empty>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {users.map((u) => (
              <div
                key={u.id}
                data-testid={`admin-user-${u.id}`}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-sm truncate">{u.email}</div>
                    <div className="mt-0.5 font-mono-plex text-xs text-slate-500 truncate">
                      LP #{u.loyal_primus_client_id}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-1 rounded-md text-[9px] font-mono-plex tracking-widest uppercase ${userStatusClass(u.status)}`}
                  >
                    {u.status}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono-plex text-slate-500">
                  <div className="truncate">
                    <span className="text-slate-400">TG · </span>{u.telegram_username || "—"}
                  </div>
                  <div className="text-right">
                    {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  {u.status !== "approved" && (
                    <button
                      data-testid={`approve-${u.id}`}
                      onClick={() => approve(u.id)}
                      className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <Check size={12} /> Approve
                    </button>
                  )}
                  {u.status === "approved" && (
                    <>
                      <button
                        data-testid={`view-journal-${u.id}`}
                        onClick={() => openJournal(u.id)}
                        className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-md text-xs font-semibold"
                        style={{ transition: "background-color 0.2s ease" }}
                      >
                        <Eye size={12} /> Journal
                      </button>
                      <button
                        data-testid={`toggle-master-${u.id}`}
                        onClick={() => toggleMaster(u)}
                        className={`flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-xs font-semibold ${u.is_master ? "bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100" : "bg-white border border-slate-200 text-slate-700 hover:border-amber-400 hover:text-amber-700"}`}
                        style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
                      >
                        <Star size={12} className={u.is_master ? "fill-amber-400" : ""} /> {u.is_master ? "Unmaster" : "Master"}
                      </button>
                    </>
                  )}
                  <button
                    data-testid={`edit-${u.id}`}
                    onClick={() => setEditTarget(u)}
                    className="inline-flex items-center justify-center gap-1 bg-white border border-slate-200 hover:border-[#00b4ff] hover:text-[#00b4ff] text-slate-700 px-3 py-2 rounded-md text-xs font-semibold"
                    style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    data-testid={`reject-${u.id}`}
                    onClick={() => openReject(u)}
                    className="inline-flex items-center justify-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                    style={{ transition: "background-color 0.2s ease" }}
                  >
                    <X size={12} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table with horizontal scroll fallback */}
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 font-mono-plex text-[10px] tracking-widest uppercase">
                <tr>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">LP Client ID</th>
                  <th className="text-left py-3 px-4">Telegram</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Registered</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 align-middle" data-testid={`admin-user-row-${u.id}`}>
                    <td className="py-2.5 px-4 font-display font-semibold text-sm align-middle">{u.email}</td>
                    <td className="py-2.5 px-4 font-mono-plex text-xs align-middle">{u.loyal_primus_client_id}</td>
                    <td className="py-2.5 px-4 font-mono-plex text-xs text-slate-500 align-middle">{u.telegram_username}</td>
                    <td className="py-2.5 px-4 align-middle">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase ${userStatusClass(u.status)}`}>{u.status}</span>
                    </td>
                    <td className="py-2.5 px-4 text-slate-500 font-mono-plex text-xs align-middle">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 px-4 text-right align-middle">
                      <div className="inline-flex gap-1 items-center whitespace-nowrap">
                        {u.status !== "approved" && (
                          <button data-testid={`approve-row-${u.id}`} onClick={() => approve(u.id)} title="Approve" aria-label="Approve" className="inline-flex items-center justify-center w-8 h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md" style={{ transition: "background-color 0.2s ease" }}>
                            <Check size={14} />
                          </button>
                        )}
                        {u.status === "approved" && (
                          <>
                            <button data-testid={`view-journal-row-${u.id}`} onClick={() => openJournal(u.id)} title="Journal" aria-label="Journal" className="inline-flex items-center justify-center w-8 h-8 bg-slate-900 hover:bg-slate-800 text-white rounded-md" style={{ transition: "background-color 0.2s ease" }}>
                              <Eye size={14} />
                            </button>
                            <button data-testid={`toggle-master-row-${u.id}`} onClick={() => toggleMaster(u)} title={u.is_master ? "Unmaster" : "Set as Master"} aria-label={u.is_master ? "Unmaster" : "Set as Master"} className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${u.is_master ? "bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100" : "bg-white border border-slate-200 text-slate-500 hover:border-amber-400 hover:text-amber-700"}`} style={{ transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease" }}>
                              <Star size={14} className={u.is_master ? "fill-amber-400" : ""} />
                            </button>
                          </>
                        )}
                        <button data-testid={`edit-row-${u.id}`} onClick={() => setEditTarget(u)} title="Edit" aria-label="Edit" className="inline-flex items-center justify-center w-8 h-8 bg-white border border-slate-200 hover:border-[#00b4ff] hover:text-[#00b4ff] text-slate-500 rounded-md" style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}>
                          <Pencil size={14} />
                        </button>
                        <button data-testid={`reject-row-${u.id}`} onClick={() => openReject(u)} title="Reject" aria-label="Reject" className="inline-flex items-center justify-center w-8 h-8 bg-rose-600 hover:bg-rose-700 text-white rounded-md" style={{ transition: "background-color 0.2s ease" }}>
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination footer */}
      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="text-xs font-mono-plex tracking-widest uppercase text-slate-500" data-testid="user-page-info">
            Showing {startIdx}–{endIdx} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="user-page-prev"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-full text-xs font-mono-plex tracking-widest uppercase border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Prev
            </button>
            <span className="text-xs font-mono-plex tracking-widest uppercase text-slate-700" data-testid="user-page-current">
              {page} / {pages}
            </span>
            <button
              data-testid="user-page-next"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages || loading}
              className="px-3 py-1.5 rounded-full text-xs font-mono-plex tracking-widest uppercase border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Bulk approve confirmation */}
      <Dialog open={bulkApproveOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkApproveOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Approve all pending users?</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              Ini akan approve {total} user pending sekaligus.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-2">
            <button
              type="button"
              onClick={() => setBulkApproveOpen(false)}
              disabled={bulkBusy}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="bulk-approve-confirm"
              onClick={doBulkApprove}
              disabled={bulkBusy}
              className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {bulkBusy ? "Approving…" : `Approve ${total}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk reject — hard delete all pending users */}
      <Dialog open={bulkRejectOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkRejectOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject all pending users?</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              Semua {total} user pending akan dibuang. Mereka boleh register semula guna email / Client ID yang sama.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-2">
            <button
              type="button"
              onClick={() => setBulkRejectOpen(false)}
              disabled={bulkBusy}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="bulk-reject-confirm"
              onClick={doBulkReject}
              disabled={bulkBusy}
              className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {bulkBusy ? "Rejecting…" : `Reject ${total}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-user reject — hard delete so they can re-register */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o && !rejectBusy) setRejectTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="user-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject user?</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              {rejectTarget?.email} akan dibuang dari sistem. Mereka boleh register semula guna email / Client ID yang sama.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-2">
            <button
              type="button"
              onClick={() => setRejectTarget(null)}
              disabled={rejectBusy}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-60"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="user-reject-confirm"
              onClick={submitReject}
              disabled={rejectBusy}
              className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {rejectBusy ? "Rejecting…" : "Confirm reject"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditUserDialog user={editTarget} onOpenChange={() => setEditTarget(null)} onSaved={() => load()} />
      <NewUserDialog open={newUserOpen} onOpenChange={setNewUserOpen} onCreated={() => load()} />
      <UserJournalDialog open={journalOpen} onOpenChange={setJournalOpen} userId={journalUserId} />
    </div>
  );
}

function UploadEbook() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", access: "free" });
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/ebooks"); setItems(data); } catch (err) { logDev(err); } };
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/content-stats/ebook").then(({ data }) => setStats(data)).catch(logDev); }, []);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Sila pilih PDF file.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("description", form.description);
      fd.append("access", form.access);
      fd.append("file", file);
      if (cover) fd.append("cover", cover);
      await api.post("/ebooks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ebook uploaded.");
      setForm({ title: "", description: "", access: "free" }); setFile(null); setCover(null);
      load();
    } catch (_) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/ebooks/${id}`); load(); };
  const [coverBusy, setCoverBusy] = useState(null);
  const changeCover = async (b, f) => {
    if (!f) return;
    setCoverBusy(b.id);
    try {
      const fd = new FormData(); fd.append("cover", f);
      await api.put(`/ebooks/${b.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated."); load();
    } catch { toast.error("Cover upload failed."); }
    finally { setCoverBusy(null); }
  };
  const toggleAccess = async (b) => {
    const next = b.access === "locked" ? "free" : "locked";
    try {
      await api.patch(`/ebooks/${b.id}`, { access: next });
      toast.success(next === "locked" ? "Ebook locked." : "Ebook is now free for all.");
      load();
    } catch { toast.error("Update failed."); }
  };
  const [editing, setEditing] = useState(null);
  const doSave = async (payload) => {
    try { await api.patch(`/ebooks/${editing.id}`, payload); toast.success("Ebook updated."); load(); }
    catch { toast.error("Update failed."); }
  };

  return (
    <div>
      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="ebook-form">
        <input required data-testid="ebook-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-3 rounded-lg border border-slate-200" />
        <input data-testid="ebook-description" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-4 py-3 rounded-lg border border-slate-200" />
        <FilePicker label="PDF file" accept="application/pdf" onFile={setFile} file={file} testid="ebook-file" />
        <FilePicker label="Cover image (optional)" accept="image/*" onFile={setCover} file={cover} testid="ebook-cover" />
        <div className="md:col-span-2">
          <div className="text-xs font-mono-plex tracking-widest uppercase text-slate-500 mb-2">Access</div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "free", label: "Free for all", icon: Unlock },
              { key: "locked", label: "Locked · request required", icon: Lock },
            ].map((opt) => {
              const active = form.access === opt.key;
              const Ico = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`ebook-access-${opt.key}`}
                  onClick={() => setForm({ ...form, access: opt.key })}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-mono-plex text-[11px] tracking-widest uppercase font-semibold ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
                >
                  <Ico size={12} /> {opt.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Locked → user perlu request unlock, admin review dalam tab <span className="font-semibold">Ebook Requests</span>.
          </p>
        </div>
        <button data-testid="ebook-upload-btn" disabled={busy} className="md:col-span-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white font-display font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2 w-max" style={{ transition: "background-color 0.2s ease" }}>
          <Upload size={14} /> {busy ? "Uploading…" : "Upload ebook"}
        </button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((e) => {
          const isLocked = e.access === "locked";
          return (
            <div key={e.id} className="min-w-0 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-ebook-${e.id}`}>
              <div className="flex items-center justify-between gap-3">
                <label className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-slate-100 cursor-pointer group" data-testid={`change-ebook-cover-${e.id}`} title="Change cover">
                  <EbookCover path={e.cover_path} fallback={EBOOK_COVER_FALLBACK} className="w-full h-full object-cover" testId={`admin-ebook-cover-${e.id}`} />
                  <span className="absolute inset-0 grid place-items-center bg-slate-900/40 text-white opacity-100 md:opacity-0 md:group-hover:opacity-100" style={{ transition: "opacity 0.2s ease" }}>
                    {coverBusy === e.id ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                  </span>
                  <input type="file" accept="image/*" className="hidden" data-testid={`change-ebook-cover-input-${e.id}`}
                         onChange={(ev) => { changeCover(e, ev.target.files?.[0]); ev.target.value = ""; }} />
                </label>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold truncate">{e.title}</div>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono-plex tracking-widest uppercase ${isLocked ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {isLocked ? <><Lock size={9} /> LOCKED</> : <><Unlock size={9} /> FREE</>}
                    </span>
                  </div>
                  <div className="text-xs font-mono-plex text-slate-500 truncate mt-0.5">{e.original_filename}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`toggle-ebook-access-${e.id}`}
                    onClick={() => toggleAccess(e)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase font-semibold ${isLocked ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                    style={{ transition: "background-color 0.2s ease" }}
                    aria-label={isLocked ? "Set to free" : "Lock ebook"}
                  >
                    {isLocked ? <Unlock size={11} /> : <Lock size={11} />} {isLocked ? "Free" : "Lock"}
                  </button>
                  <button data-testid={`rename-ebook-${e.id}`} onClick={() => setEditing(e)} className="text-slate-400 hover:text-[#00b4ff] p-1.5" style={{ transition: "color 0.2s ease" }} aria-label="Rename">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-ebook-${e.id}`} onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600 p-1.5" style={{ transition: "color 0.2s ease" }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <ContentStatsBar kind="ebook" item={e} stats={stats[e.id]} />
            </div>
          );
        })}
      </div>
      <EditMediaDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        kind="ebook"
        initial={editing}
        onSave={doSave}
      />
    </div>
  );
}

function ManageEbookRequests() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh: refreshBadges } = useAdminPending();

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/ebook-requests${filter !== "all" ? `?status=${filter}` : ""}`);
      setReqs(data);
    } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => { await api.post(`/admin/ebook-requests/${id}/approve`); toast.success("Approved"); load(); refreshBadges(); };
  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/ebook-requests/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.warning("Rejected");
      setRejectTarget(null); setRejectReason("");
      load(); refreshBadges();
    } catch { toast.error("Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            data-testid={`ereq-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {f}
          </button>
        ))}
      </div>
      {reqs.length === 0 ? (
        <Empty>Tiada request dalam filter ini.</Empty>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ereq-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold truncate">{r.ebook?.title || "(deleted ebook)"}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono-plex tracking-widest uppercase ${
                      r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                      r.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"
                    }`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs font-mono-plex text-slate-500 truncate">
                    {r.user?.email || "unknown"} · LP #{r.user?.loyal_primus_client_id || "—"}
                  </div>
                  <div className="mt-0.5 text-[10px] font-mono-plex tracking-widest text-slate-400 uppercase">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.status === "rejected" && r.reject_reason && (
                    <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                      Reject reason: {r.reject_reason}
                    </div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`ereq-approve-${r.id}`}
                      onClick={() => approve(r.id)}
                      className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      data-testid={`ereq-reject-${r.id}`}
                      onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                      className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="ereq-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject ebook unlock</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              {rejectTarget?.user?.email} · {rejectTarget?.ebook?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="space-y-3 pt-2">
            <textarea
              data-testid="ereq-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Sebab reject (optional but recommended)…"
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] resize-none text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <DialogFooter className="gap-2">
              <button type="button" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                Batal
              </button>
              <button type="submit" disabled={busy} data-testid="ereq-reject-confirm" className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                {busy ? "Rejecting…" : "Confirm reject"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadVideo() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", url: "", access: "free" });
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/videos"); setItems(data); } catch (err) { logDev(err); } };
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/content-stats/video").then(({ data }) => setStats(data)).catch(logDev); }, []);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !form.url) return toast.error("Sila upload file atau paste URL.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("description", form.description);
      fd.append("access", form.access);
      if (form.url) fd.append("url", form.url);
      if (file) fd.append("file", file);
      if (cover) fd.append("cover", cover);
      await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video added.");
      setForm({ title: "", description: "", url: "", access: "free" }); setFile(null); setCover(null);
      load();
    } catch (_) { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/videos/${id}`); load(); };
  const toggleAccess = async (v) => {
    const next = v.access === "locked" ? "free" : "locked";
    try {
      await api.patch(`/videos/${v.id}`, { access: next });
      toast.success(next === "locked" ? "Video locked." : "Video is now free for all.");
      load();
    } catch { toast.error("Update failed."); }
  };
  const [editing, setEditing] = useState(null);
  const doSave = async (payload) => {
    try { await api.patch(`/videos/${editing.id}`, payload); toast.success("Video updated."); load(); }
    catch { toast.error("Update failed."); }
  };

  return (
    <div>
      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="video-form">
        <input required data-testid="video-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-3 rounded-lg border border-slate-200" />
        <input data-testid="video-description" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-4 py-3 rounded-lg border border-slate-200" />
        <div className="md:col-span-2">
          <input data-testid="video-url" placeholder="Paste Google Drive / YouTube / Vimeo URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200" />
          <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
            Google Drive: pastikan file sharing set kepada <span className="font-semibold">&ldquo;Anyone with the link — Viewer&rdquo;</span>. Paste share link biasa (contoh <span className="font-mono-plex">drive.google.com/file/d/…/view</span>) — app akan auto convert kepada preview embed.
          </p>
        </div>
        <FilePicker label="Video file (mp4)" accept="video/*" onFile={setFile} file={file} testid="video-file" />
        <FilePicker label="Cover image (optional)" accept="image/*" onFile={setCover} file={cover} testid="video-cover" />
        <div className="md:col-span-2">
          <div className="text-xs font-mono-plex tracking-widest uppercase text-slate-500 mb-2">Access</div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "free", label: "Free for all", icon: Unlock },
              { key: "locked", label: "Locked · request required", icon: Lock },
            ].map((opt) => {
              const active = form.access === opt.key;
              const Ico = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`video-access-${opt.key}`}
                  onClick={() => setForm({ ...form, access: opt.key })}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-mono-plex text-[11px] tracking-widest uppercase font-semibold ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
                >
                  <Ico size={12} /> {opt.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Locked → user perlu request unlock, admin akan review dalam tab <span className="font-semibold">Video Requests</span>.
          </p>
        </div>
        <button data-testid="video-upload-btn" disabled={busy} className="md:col-span-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white font-display font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2 w-max" style={{ transition: "background-color 0.2s ease" }}>
          <Upload size={14} /> {busy ? "Uploading…" : "Save video"}
        </button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((v) => {
          const isLocked = v.access === "locked";
          return (
            <div key={v.id} className="min-w-0 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-video-${v.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold truncate">{v.title}</div>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono-plex tracking-widest uppercase ${isLocked ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {isLocked ? <><Lock size={9} /> LOCKED</> : <><Unlock size={9} /> FREE</>}
                    </span>
                  </div>
                  <div className="text-xs font-mono-plex text-slate-500 truncate mt-0.5">{v.url || v.storage_path}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`toggle-video-access-${v.id}`}
                    onClick={() => toggleAccess(v)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase font-semibold ${isLocked ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                    style={{ transition: "background-color 0.2s ease" }}
                    aria-label={isLocked ? "Set to free" : "Lock video"}
                  >
                    {isLocked ? <Unlock size={11} /> : <Lock size={11} />} {isLocked ? "Free" : "Lock"}
                  </button>
                  <button data-testid={`rename-video-${v.id}`} onClick={() => setEditing(v)} className="text-slate-400 hover:text-[#00b4ff] p-1.5" style={{ transition: "color 0.2s ease" }} aria-label="Rename">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`delete-video-${v.id}`} onClick={() => del(v.id)} className="text-slate-400 hover:text-rose-600 p-1.5" style={{ transition: "color 0.2s ease" }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <ContentStatsBar kind="video" item={v} stats={stats[v.id]} canDownload={!v.url} />
            </div>
          );
        })}
      </div>
      <EditMediaDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        kind="video"
        initial={editing}
        onSave={doSave}
      />
    </div>
  );
}

function ManageVideoRequests() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh: refreshBadges } = useAdminPending();

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/video-requests${filter !== "all" ? `?status=${filter}` : ""}`);
      setReqs(data);
    } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => { await api.post(`/admin/video-requests/${id}/approve`); toast.success("Approved"); load(); refreshBadges(); };
  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/video-requests/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.warning("Rejected");
      setRejectTarget(null); setRejectReason("");
      load(); refreshBadges();
    } catch { toast.error("Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            data-testid={`vreq-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {f}
          </button>
        ))}
      </div>
      {reqs.length === 0 ? (
        <Empty>Tiada request dalam filter ini.</Empty>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`vreq-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold truncate">{r.video?.title || "(deleted video)"}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono-plex tracking-widest uppercase ${
                      r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                      r.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"
                    }`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs font-mono-plex text-slate-500 truncate">
                    {r.user?.email || "unknown"} · LP #{r.user?.loyal_primus_client_id || "—"}
                  </div>
                  <div className="mt-0.5 text-[10px] font-mono-plex tracking-widest text-slate-400 uppercase">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.status === "rejected" && r.reject_reason && (
                    <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                      Reject reason: {r.reject_reason}
                    </div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`vreq-approve-${r.id}`}
                      onClick={() => approve(r.id)}
                      className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      data-testid={`vreq-reject-${r.id}`}
                      onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                      className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="vreq-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject video unlock</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              {rejectTarget?.user?.email} · {rejectTarget?.video?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="space-y-3 pt-2">
            <textarea
              data-testid="vreq-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Sebab reject (optional but recommended)…"
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] resize-none text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <DialogFooter className="gap-2">
              <button type="button" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                Batal
              </button>
              <button type="submit" disabled={busy} data-testid="vreq-reject-confirm" className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                {busy ? "Rejecting…" : "Confirm reject"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Pre-filled signal templates admin can broadcast in two taps.
const SIGNAL_TEMPLATES = [
  { key: "xauusd-buy", label: "XAUUSD · BUY", pair: "XAUUSD", direction: "buy" },
  { key: "xauusd-sell", label: "XAUUSD · SELL", pair: "XAUUSD", direction: "sell" },
  { key: "btcusd-buy", label: "BTCUSD · BUY", pair: "BTCUSD", direction: "buy" },
  { key: "btcusd-sell", label: "BTCUSD · SELL", pair: "BTCUSD", direction: "sell" },
];

function ManageSignals() {
  const [signals, setSignals] = useState([]);
  const [form, setForm] = useState({ pair: "", direction: "buy", entry: "", tp: "", sl: "", note: "" });

  const load = async () => { try { const { data } = await api.get("/signals"); setSignals(data); } catch (err) { logDev(err); } };
  useEffect(() => { load(); }, []);

  const applyTemplate = (tpl) => {
    setForm((prev) => ({ ...prev, pair: tpl.pair, direction: tpl.direction }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/signals", {
        pair: form.pair, direction: form.direction, entry: form.entry ? parseFloat(form.entry) : null,
        tp: parseFloat(form.tp), sl: parseFloat(form.sl), note: form.note,
      });
      toast.success("Signal broadcast.");
      setForm({ pair: "", direction: "buy", entry: "", tp: "", sl: "", note: "" });
      load();
    } catch (_) { toast.error("Failed."); }
  };

  const setStatus = async (id, status) => { await api.patch(`/signals/${id}`, { status }); toast.success(`Marked ${status.toUpperCase()}`); load(); };
  const del = async (id) => { await api.delete(`/signals/${id}`); load(); };

  return (
    <div>
      {/* Quick templates — one tap fills pair + direction */}
      <div className="mb-3 flex flex-wrap gap-2" data-testid="signal-templates">
        <span className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-500 self-center mr-1">Quick fill</span>
        {SIGNAL_TEMPLATES.map((tpl) => {
          const active = form.pair === tpl.pair && form.direction === tpl.direction;
          const isBuy = tpl.direction === "buy";
          const base = isBuy
            ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            : "border-rose-200 text-rose-700 hover:bg-rose-50";
          const activeCls = active
            ? (isBuy ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700" : "bg-rose-600 border-rose-600 text-white hover:bg-rose-700")
            : `bg-white ${base}`;
          return (
            <button
              key={tpl.key}
              type="button"
              onClick={() => applyTemplate(tpl)}
              data-testid={`signal-template-${tpl.key}`}
              className={`px-3 py-1.5 rounded-full border font-mono-plex text-[11px] tracking-widest uppercase font-semibold ${activeCls}`}
              style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
            >
              {tpl.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 grid md:grid-cols-6 gap-3" data-testid="signal-form">
        <input required data-testid="signal-pair" placeholder="XAUUSD" value={form.pair} onChange={(e) => setForm({ ...form, pair: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
        <select data-testid="signal-direction" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex">
          <option value="buy">BUY</option><option value="sell">SELL</option>
        </select>
        <input data-testid="signal-entry" placeholder="Entry" type="number" step="0.0001" value={form.entry} onChange={(e) => setForm({ ...form, entry: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
        <input required data-testid="signal-tp" placeholder="TP" type="number" step="0.0001" value={form.tp} onChange={(e) => setForm({ ...form, tp: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
        <input required data-testid="signal-sl" placeholder="SL" type="number" step="0.0001" value={form.sl} onChange={(e) => setForm({ ...form, sl: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
        <button data-testid="signal-broadcast-btn" className="bg-[#00b4ff] hover:bg-[#0090cc] text-white rounded-lg px-4 py-2.5 font-semibold text-sm inline-flex items-center justify-center gap-2" style={{ transition: "background-color 0.2s ease" }}>
          <Plus size={14} /> Broadcast
        </button>
        <textarea data-testid="signal-note" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 md:col-span-6" rows="2" />
      </form>

      {signals.length === 0 ? <Empty>No signals yet.</Empty> : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {signals.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-signal-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-lg tracking-tight">{s.pair}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase ${s.direction === "buy" ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}>
                        {s.direction}
                      </span>
                    </div>
                    <div className="mt-1 font-mono-plex text-xs">
                      TP <span className="text-emerald-700 font-semibold">{s.tp}</span>
                      <span className="mx-2 text-slate-300">/</span>
                      SL <span className="text-rose-700 font-semibold">{s.sl}</span>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-slate-100">{s.status.replace("_", " ")}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.status === "active" && (
                    <>
                      <button data-testid={`sig-tp-${s.id}`} onClick={() => setStatus(s.id, "hit_tp")} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-2 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>TP</button>
                      <button data-testid={`sig-sl-${s.id}`} onClick={() => setStatus(s.id, "hit_sl")} className="flex-1 bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-2 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>SL</button>
                      <button data-testid={`sig-cx-${s.id}`} onClick={() => setStatus(s.id, "cancelled")} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-2 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>Cancel</button>
                    </>
                  )}
                  <button data-testid={`sig-del-${s.id}`} onClick={() => del(s.id)} className="px-3 py-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }} aria-label="Delete signal">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table (with horizontal scroll fallback) */}
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500 font-mono-plex text-[10px] tracking-widest uppercase">
                <tr>
                  <th className="text-left py-3 px-4">Pair</th>
                  <th className="text-left py-3 px-4">Dir</th>
                  <th className="text-left py-3 px-4">TP / SL</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signals.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`admin-signal-row-${s.id}`}>
                    <td className="py-3 px-4 font-display font-semibold">{s.pair}</td>
                    <td className="py-3 px-4 font-mono-plex uppercase text-xs">{s.direction}</td>
                    <td className="py-3 px-4 font-mono-plex text-xs">
                      <span className="text-emerald-700">{s.tp}</span> / <span className="text-rose-700">{s.sl}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-slate-100">{s.status.replace("_", " ")}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex gap-1.5 items-center">
                        {s.status === "active" && (
                          <>
                            <button data-testid={`sig-tp-row-${s.id}`} onClick={() => setStatus(s.id, "hit_tp")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>TP</button>
                            <button data-testid={`sig-sl-row-${s.id}`} onClick={() => setStatus(s.id, "hit_sl")} className="bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>SL</button>
                            <button data-testid={`sig-cx-row-${s.id}`} onClick={() => setStatus(s.id, "cancelled")} className="bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase" style={{ transition: "background-color 0.2s ease" }}>Cancel</button>
                          </>
                        )}
                        <button data-testid={`sig-del-row-${s.id}`} onClick={() => del(s.id)} className="text-slate-400 hover:text-rose-600 p-1" style={{ transition: "color 0.2s ease" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ManageBookings() {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ date: "", time: "", capacity: 1, title: "Coaching Session", requirements: "", requires_redeposit: false });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyReject, setBusyReject] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([api.get("/slots"), api.get("/bookings")]);
      setSlots(s.data); setBookings(b.data);
    } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); }, []);

  const addSlot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/slots", { ...form, capacity: parseInt(form.capacity, 10) });
      toast.success("Slot added.");
      setForm({ date: "", time: "", capacity: 1, title: "Coaching Session", requirements: "", requires_redeposit: false }); load();
    } catch (_) { toast.error("Failed."); }
  };
  const delSlot = async (id) => { await api.delete(`/slots/${id}`); load(); };
  const accept = async (id) => { await api.post(`/bookings/${id}/accept`); load(); };
  const openReject = (b) => { setRejectTarget(b); setRejectReason(""); };
  const confirmReject = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    setBusyReject(true);
    try {
      await api.post(`/bookings/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.warning("Booking rejected.");
      setRejectTarget(null); setRejectReason("");
      load();
    } catch (_) { toast.error("Reject failed."); }
    finally { setBusyReject(false); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel booking user ini? Seat akan dibebaskan.")) return;
    await api.post(`/bookings/${id}/cancel`); toast.warning("Booking cancelled."); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <div className="font-display font-semibold mb-3 tracking-tight">Coaching slots</div>
        <form onSubmit={addSlot} className="rounded-2xl border border-slate-200 bg-white p-5 mb-4 grid grid-cols-2 gap-3" data-testid="slot-form">
          <input required type="date" data-testid="slot-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
          <input required type="time" data-testid="slot-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
          <input required type="number" min="1" data-testid="slot-capacity" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200 font-mono-plex" />
          <input data-testid="slot-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2.5 rounded-lg border border-slate-200" />
          <textarea data-testid="slot-requirements" placeholder="Syarat untuk join coaching (contoh: sudah selesai ebook 1, minimum deposit $100, redeposit $50, dll.)" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} className="col-span-2 px-3 py-2.5 rounded-lg border border-slate-200 text-sm leading-relaxed" rows="3" />
          <label className="col-span-2 inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" data-testid="slot-requires-redeposit" checked={form.requires_redeposit} onChange={(e) => setForm({ ...form, requires_redeposit: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-[#00b4ff] focus:ring-[#00b4ff]" />
            <span className="text-sm text-slate-700">Require redeposit — user perlu masukkan nilai redeposit semasa apply booking.</span>
          </label>
          <button data-testid="slot-add-btn" className="col-span-2 bg-[#00b4ff] hover:bg-[#0090cc] text-white rounded-full px-4 py-2.5 font-semibold text-sm" style={{ transition: "background-color 0.2s ease" }}>Add slot</button>
        </form>
        {slots.length === 0 ? <Empty>No slots yet.</Empty> : (
          <div className="space-y-2">
            {slots.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-slot-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-plex text-sm">{s.date} · {s.time}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.title} — {s.booked_count || 0}/{s.capacity} seats</div>
                    {s.requires_redeposit && (
                      <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-amber-50 text-amber-700">
                        Redeposit required
                      </span>
                    )}
                    {s.requirements && (
                      <p className="mt-2 text-xs text-slate-600 whitespace-pre-wrap leading-relaxed border-l-2 border-[#00b4ff] pl-3">{s.requirements}</p>
                    )}
                  </div>
                  <button onClick={() => delSlot(s.id)} className="text-slate-400 hover:text-rose-600 shrink-0" style={{ transition: "color 0.2s ease" }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="font-display font-semibold mb-3 tracking-tight">Bookings</div>
        {bookings.length === 0 ? <Empty>No bookings.</Empty> : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-booking-${b.id}`}>
                <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1 w-full">
                    <div className="font-display font-semibold truncate">{b.user?.email || "—"}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 font-mono-plex">
                      {b.user?.loyal_primus_client_id && (
                        <span data-testid={`booking-lp-id-${b.id}`}>
                          LP ID: <span className="text-slate-900 font-semibold">{b.user.loyal_primus_client_id}</span>
                        </span>
                      )}
                      {b.user?.telegram_username && (
                        <span>TG: @{b.user.telegram_username.replace(/^@/, "")}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono-plex mt-1">
                      {b.slot?.date} · {b.slot?.time} — {b.slot?.title}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`inline-flex px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase ${
                        b.status === "accepted" ? "bg-emerald-50 text-emerald-700" :
                        b.status === "rejected" ? "bg-rose-50 text-rose-700" :
                        b.status === "cancelled" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"
                      }`}>{b.status}</span>
                      {b.redeposit_amount != null && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-[#00b4ff]/10 text-[#00b4ff]" data-testid={`booking-redeposit-${b.id}`}>
                          Redeposit ${Number(b.redeposit_amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {b.slot?.requirements && (
                      <p className="mt-2 text-[11px] text-slate-500 leading-relaxed border-l-2 border-slate-200 pl-2">
                        <span className="font-semibold text-slate-700">Syarat:</span> {b.slot.requirements}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-row sm:flex-col gap-1.5 shrink-0 w-full sm:w-auto">
                    {b.status === "pending" && (
                      <>
                        <button data-testid={`bk-accept-${b.id}`} onClick={() => accept(b.id)} className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 sm:py-1.5 rounded-md text-xs font-semibold" style={{ transition: "background-color 0.2s ease" }}>Accept</button>
                        <button data-testid={`bk-reject-${b.id}`} onClick={() => openReject(b)} className="flex-1 sm:flex-none bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 sm:py-1.5 rounded-md text-xs font-semibold" style={{ transition: "background-color 0.2s ease" }}>Reject</button>
                      </>
                    )}
                    {b.status === "accepted" && (
                      <button data-testid={`bk-cancel-${b.id}`} onClick={() => cancel(b.id)} className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 sm:py-1.5 rounded-md text-xs font-semibold" style={{ transition: "background-color 0.2s ease" }}>Cancel</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject reason dialog */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-5" data-testid="reject-dialog">
          <form onSubmit={confirmReject} className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-rose-600">Reject booking</div>
            <h3 className="mt-2 font-display text-xl font-bold tracking-tight">
              {rejectTarget.user?.email || "user"}
            </h3>
            <p className="mt-1 text-xs text-slate-500 font-mono-plex">
              {rejectTarget.slot?.date} · {rejectTarget.slot?.time} — {rejectTarget.slot?.title}
            </p>
            <div className="mt-5">
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Sebab reject (optional)</label>
              <textarea
                data-testid="reject-reason-input"
                autoFocus
                rows="4"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Contoh: Redeposit belum sampai. Sila top-up dahulu dan apply semula."
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-400"
              />
              <p className="mt-1.5 text-[11px] text-slate-500">User akan nampak sebab ini dalam announcements mereka.</p>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="px-5 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }} data-testid="reject-cancel-btn">
                Batal
              </button>
              <button type="submit" disabled={busyReject} data-testid="reject-confirm-btn" className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                {busyReject ? "Rejecting…" : "Confirm reject"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FilePicker({ label, accept, onFile, file, testid }) {
  return (
    <label className="block cursor-pointer">
      <div className="text-xs font-mono-plex tracking-widest uppercase text-slate-500 mb-1.5">{label}</div>
      <div className="px-4 py-3 rounded-lg border border-dashed border-slate-300 hover:border-[#00b4ff] bg-slate-50 text-sm text-slate-600 truncate" style={{ transition: "border-color 0.2s ease" }} data-testid={testid}>
        {file ? file.name : "Click to select…"}
      </div>
      <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
    </label>
  );
}

function Empty({ children }) {
  return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">{children}</div>;
}

function fmtLastLogin(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}

function fmtMoney(n) {
  const num = Number(n) || 0;
  const sign = num < 0 ? "-" : "";
  return `${sign}$${Math.abs(num).toFixed(2)}`;
}

function UserJournalDialog({ open, onOpenChange, userId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/users/${userId}/journal`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load journal"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const stats = data?.stats;
  const winRate = stats && stats.trades_count > 0 ? Math.round((stats.wins / stats.trades_count) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-journal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {data?.user?.name || data?.user?.email || "Trade Journal"}
          </DialogTitle>
          <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
            {data?.user?.email} · LP #{data?.user?.loyal_primus_client_id || "—"}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}

        {!loading && data && (
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Trade P&L</div>
                <div className={`mt-1 font-display font-bold text-lg ${stats.trade_pnl >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoney(stats.trade_pnl)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Win rate</div>
                <div className="mt-1 font-display font-bold text-lg">{winRate}%</div>
                <div className="text-[10px] font-mono-plex text-slate-500">{stats.wins}W / {stats.losses}L</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Deposits</div>
                <div className="mt-1 font-display font-bold text-lg text-[#00b4ff]">{fmtMoney(stats.deposits)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Withdrawals</div>
                <div className="mt-1 font-display font-bold text-lg text-slate-700">{fmtMoney(stats.withdrawals)}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[11px] font-mono-plex text-slate-500">
              <Clock size={12} /> Last login: <span className="text-slate-700 font-semibold">{fmtLastLogin(data.user.last_login_at)}</span>
            </div>

            <div>
              <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 mb-2">Entries ({data.entries.length})</div>
              {data.entries.length === 0 ? (
                <Empty>Belum ada journal entry.</Empty>
              ) : (
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {data.entries.map((e) => {
                    const isProfit = e.kind === "trade" && e.amount >= 0;
                    const isLoss = e.kind === "trade" && e.amount < 0;
                    const isDeposit = e.kind === "deposit";
                    const label = e.kind === "trade" ? (isProfit ? "PROFIT" : "LOSS") : e.kind.toUpperCase();
                    let color = "text-slate-700";
                    if (isProfit) color = "text-emerald-700";
                    else if (isLoss) color = "text-rose-700";
                    else if (isDeposit) color = "text-[#00b4ff]";
                    return (
                      <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`admin-journal-${e.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">{e.date}</span>
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-400">·</span>
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-600">{label}</span>
                          </div>
                          {e.note && <div className="mt-0.5 text-xs text-slate-600 truncate">{e.note}</div>}
                        </div>
                        <div className={`font-mono-plex font-semibold shrink-0 ${color}`}>
                          {e.kind === "trade" ? fmtMoney(e.amount) : `$${Number(e.amount).toFixed(2)}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManageMasterRequests() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh: refreshBadges } = useAdminPending();

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/master-requests${filter !== "all" ? `?status=${filter}` : ""}`);
      setReqs(data);
    } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => { await api.post(`/admin/master-requests/${id}/approve`); toast.success("Approved"); load(); refreshBadges(); };
  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/master-requests/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.warning("Rejected");
      setRejectTarget(null); setRejectReason("");
      load(); refreshBadges();
    } catch { toast.error("Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            data-testid={`mreq-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {f}
          </button>
        ))}
      </div>
      {reqs.length === 0 ? (
        <Empty>Tiada request dalam filter ini.</Empty>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`mreq-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold truncate">
                      {r.requester?.email || "unknown"} → <Star size={12} className="inline text-amber-500 fill-amber-400" /> {r.master?.email || "unknown"}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono-plex tracking-widest uppercase ${
                      r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                      r.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"
                    }`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs font-mono-plex text-slate-500 truncate">
                    Requester LP #{r.requester?.loyal_primus_client_id || "—"} · Master LP #{r.master?.loyal_primus_client_id || "—"}
                  </div>
                  <div className="mt-0.5 text-[10px] font-mono-plex tracking-widest text-slate-400 uppercase">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.status === "rejected" && r.reject_reason && (
                    <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                      Reject reason: {r.reject_reason}
                    </div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`mreq-approve-${r.id}`}
                      onClick={() => approve(r.id)}
                      className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      data-testid={`mreq-reject-${r.id}`}
                      onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                      className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="mreq-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject master-journal access</DialogTitle>
            <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
              {rejectTarget?.requester?.email} → {rejectTarget?.master?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReject} className="space-y-3 pt-2">
            <textarea
              data-testid="mreq-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Sebab reject (optional but recommended)…"
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] resize-none text-sm"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <DialogFooter className="gap-2">
              <button type="button" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                Batal
              </button>
              <button type="submit" disabled={busy} data-testid="mreq-reject-confirm" className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                {busy ? "Rejecting…" : "Confirm reject"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminDirectMessages() {
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const scrollRef = React.useRef(null);
  const LIMIT = 20;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadThreads = async (opts = {}) => {
    const targetPage = opts.page ?? page;
    setLoadingThreads(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("q", debouncedSearch);
      params.set("page", String(targetPage));
      params.set("limit", String(LIMIT));
      const { data } = await api.get(`/admin/dm/threads?${params.toString()}`);
      setThreads(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (err) { logDev(err); }
    finally { setLoadingThreads(false); }
  };
  useEffect(() => { setPage(1); loadThreads({ page: 1 }); /* eslint-disable-next-line */ }, [debouncedSearch]);
  useEffect(() => { loadThreads({ page }); /* eslint-disable-next-line */ }, [page]);
  // Refresh threads every 10s so unread counts + last-message previews stay live.
  useEffect(() => {
    const t = setInterval(() => loadThreads(), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [debouncedSearch, page]);

  const loadThread = async (userId) => {
    try {
      const { data } = await api.get(`/admin/dm/${userId}`);
      setMessages(data.messages || []);
      setSelected(data.user);
      // Clear unread badge on this thread locally.
      setThreads((prev) => prev.map((t) => t.user.id === userId ? { ...t, unread: 0 } : t));
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        if (window.innerWidth < 768) document.querySelector('[data-testid="dm-chat-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) { toast.error("Cannot load thread"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/dm/${selected.id}`, { text: t });
      setMessages((prev) => [...prev, data]);
      setText("");
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    finally { setSending(false); }
  };

  // Poll open thread for admin's new messages every 4s (SSE also pushes; polling is a safety net).
  useEffect(() => {
    if (!selected) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/dm/${selected.id}`);
        setMessages(data.messages || []);
      } catch (_) { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [selected?.id]);

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 min-h-[560px]" data-testid="admin-direct-messages">
      {/* Thread list */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
        <div className="p-3 border-b border-slate-100 relative">
          <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            data-testid="dm-thread-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search approved users…"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm bg-slate-50"
            style={{ transition: "border-color 0.2s ease" }}
          />
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 font-mono-plex">
              {loadingThreads ? "Loading…" : "No approved users match."}
            </div>
          ) : threads.map((t) => (
            <button
              key={t.user.id}
              data-testid={`dm-thread-${t.user.id}`}
              onClick={() => loadThread(t.user.id)}
              className={`w-full text-left p-3 hover:bg-slate-50 flex gap-3 items-start ${selected?.id === t.user.id ? "bg-[#00b4ff]/5" : ""}`}
              style={{ transition: "background-color 0.2s ease" }}
            >
              <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-500 font-display font-bold text-sm shrink-0">
                {(t.user.name || t.user.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display text-sm font-semibold truncate">{t.user.name || t.user.email}</div>
                  {t.unread > 0 && (
                    <span data-testid={`dm-thread-unread-${t.user.id}`} className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
                      {t.unread > 9 ? "9+" : t.unread}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-500 font-mono-plex truncate">
                  {t.last_message ? `${t.last_message.sender === "admin" ? "You: " : ""}${t.last_message.text}` : "No messages yet"}
                </div>
              </div>
            </button>
          ))}
        </div>
        {pages > 1 && (
          <div className="p-2 border-t border-slate-100 flex items-center justify-between text-[10px] font-mono-plex tracking-widest uppercase">
            <button data-testid="dm-thread-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-1 rounded-full border border-slate-200 disabled:opacity-40">Prev</button>
            <span>{page} / {pages} · {total}</span>
            <button data-testid="dm-thread-next" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="px-2 py-1 rounded-full border border-slate-200 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {/* Chat panel */}
      <div className="rounded-2xl border border-slate-200 bg-white flex flex-col min-h-[500px]" data-testid="dm-chat-panel">
        {!selected ? (
          <div className="flex-1 grid place-items-center text-center p-8">
            <div>
              <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center mx-auto text-slate-500">
                <MessageCircle size={22} />
              </div>
              <div className="mt-4 font-display font-semibold text-slate-700">Select a user to start chatting</div>
              <div className="mt-1 text-xs text-slate-500 font-mono-plex">Direct messages are 1-to-1 with approved users.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center text-slate-500 font-display font-bold">
                {(selected.name || selected.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm truncate" data-testid="dm-chat-user-name">{selected.name || selected.email}</div>
                <div className="text-[11px] font-mono-plex text-slate-500 truncate">{selected.email}{selected.telegram_username ? ` · @${selected.telegram_username.replace(/^@/, "")}` : ""}</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
              {messages.length === 0 ? (
                <div className="text-center text-xs font-mono-plex text-slate-500 py-8">No messages yet — send the first one below.</div>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
                  <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${m.sender === "admin" ? "bg-[#00b4ff] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                    {m.text}
                    <div className={`mt-1 text-[10px] font-mono-plex ${m.sender === "admin" ? "text-white/70" : "text-slate-400"}`}>
                      {new Date(m.created_at).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
              <input
                data-testid="dm-chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                maxLength={2000}
                className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
                style={{ transition: "border-color 0.2s ease" }}
              />
              <button
                type="submit"
                data-testid="dm-chat-send"
                disabled={sending || !text.trim()}
                className="inline-flex items-center gap-1.5 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white px-4 py-2.5 rounded-full text-sm font-semibold"
                style={{ transition: "background-color 0.2s ease" }}
              >
                <Send size={14} /> {sending ? "Sending…" : "Send"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}


function ManageAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", type: "info" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements?limit=100");
      setItems(data);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const message = form.message.trim();
    if (!title || !message) { toast.error("Title dan message wajib diisi."); return; }
    setBusy(true);
    try {
      await api.post("/announcements", { title, message, type: form.type });
      toast.success("Broadcast dihantar kepada semua user");
      setForm({ title: "", message: "", type: "info" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast gagal");
    }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.warning("Announcement deleted");
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch (_) { toast.error("Delete failed"); }
  };

  const globals = items.filter((a) => !a.user_id);
  const personal = items.filter((a) => a.user_id);

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="manage-announcements">
      {/* Compose panel */}
      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="broadcast-form">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Compose</div>
          <h3 className="mt-1 font-display text-xl font-bold tracking-tight">New Broadcast</h3>
          <p className="mt-1 text-xs text-slate-500">Hantar kepada semua approved users sekaligus. Muncul di Home → Announcements.</p>
        </div>
        <div>
          <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Title</label>
          <input
            data-testid="broadcast-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={200}
            placeholder="e.g. Market update — CPI release"
            className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm"
            style={{ transition: "border-color 0.2s ease" }}
          />
        </div>
        <div>
          <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Message</label>
          <textarea
            data-testid="broadcast-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={6}
            maxLength={2000}
            placeholder="Detail penuh yang user akan baca…"
            className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#00b4ff] text-sm resize-none"
            style={{ transition: "border-color 0.2s ease" }}
          />
          <div className="mt-1 text-[10px] font-mono-plex tracking-widest uppercase text-slate-400 text-right">
            {form.message.length}/2000
          </div>
        </div>
        <div>
          <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Type</label>
          <div className="mt-2 flex gap-2 flex-wrap">
            {[
              { v: "info", label: "Info", cls: "bg-[#00b4ff] text-white border-[#00b4ff]" },
              { v: "success", label: "Success", cls: "bg-emerald-600 text-white border-emerald-600" },
              { v: "warning", label: "Warning", cls: "bg-amber-500 text-white border-amber-500" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                data-testid={`broadcast-type-${t.v}`}
                onClick={() => setForm({ ...form, type: t.v })}
                className={`px-4 py-1.5 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${form.type === t.v ? t.cls : "bg-white text-slate-600 border-slate-200"}`}
                style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          data-testid="broadcast-submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-display font-semibold px-6 py-3 rounded-full text-sm"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <Megaphone size={14} /> {busy ? "Broadcasting…" : "Broadcast to all users"}
        </button>
      </form>

      {/* History panel */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="broadcast-history">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">History</div>
            <h3 className="mt-1 font-display text-xl font-bold tracking-tight">Recent</h3>
          </div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">
            {globals.length} broadcast · {personal.length} personal
          </div>
        </div>

        <div className="mt-4 space-y-3 max-h-[520px] overflow-y-auto pr-1">
          {loading ? (
            <div className="text-center text-xs font-mono-plex text-slate-500 py-6">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-xs font-mono-plex text-slate-500 py-6">No announcements yet.</div>
          ) : items.map((a) => (
            <div
              key={a.id}
              data-testid={`announcement-item-${a.id}`}
              className="rounded-xl border border-slate-200 p-3 hover:border-slate-300 group relative"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-mono-plex tracking-widest uppercase ${
                      a.type === "success" ? "bg-emerald-50 text-emerald-700" :
                      a.type === "warning" ? "bg-amber-50 text-amber-700" : "bg-[#00b4ff]/10 text-[#00b4ff]"
                    }`}>{a.type || "info"}</span>
                    <span className={`text-[9px] font-mono-plex tracking-widest uppercase ${a.user_id ? "text-slate-500" : "text-slate-900 font-bold"}`}>
                      {a.user_id ? "Personal" : "Broadcast"}
                    </span>
                    <span className="text-[10px] font-mono-plex text-slate-400 ml-auto">
                      {new Date(a.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="mt-1.5 font-display font-semibold text-sm truncate">{a.title}</div>
                  <div className="mt-0.5 text-xs text-slate-600 whitespace-pre-wrap line-clamp-3">{a.message}</div>
                </div>
                <button
                  type="button"
                  data-testid={`delete-announcement-${a.id}`}
                  onClick={() => remove(a.id)}
                  aria-label="Delete"
                  className="shrink-0 w-7 h-7 rounded-full grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100"
                  style={{ transition: "background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease" }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function AdminPage() {
  const { counts: adminCounts } = useAdminPending();
  const vBadge = adminCounts.video_requests || 0;
  const eBadge = adminCounts.ebook_requests || 0;
  const mBadge = adminCounts.master_requests || 0;
  const [dmBadge, setDmBadge] = useState(0);
  const [tab, setTab] = useState("users");
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get("/admin/dm-unread-count");
        if (!cancelled) setDmBadge(data.count || 0);
      } catch (_) { /* ignore transient */ }
    };
    refresh();
    const t = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const TAB_OPTIONS = [
    { value: "users", label: "Approve Users", icon: Users, badge: 0 },
    { value: "messages", label: "Messages", icon: MessageCircle, badge: dmBadge },
    { value: "broadcast", label: "Broadcast", icon: Megaphone, badge: 0 },
    { value: "ebooks", label: "Ebooks", icon: BookOpen, badge: 0 },
    { value: "ebook-requests", label: "Ebook Requests", icon: MessageSquareWarning, badge: eBadge },
    { value: "videos", label: "Videos", icon: PlayCircle, badge: 0 },
    { value: "video-requests", label: "Video Requests", icon: MessageSquareWarning, badge: vBadge },
    { value: "signals", label: "Signals", icon: Radio, badge: 0 },
    { value: "bookings", label: "Coaching", icon: CalendarClock, badge: 0 },
    { value: "master-requests", label: "Master Requests", icon: Star, badge: mBadge },
  ];
  const totalOtherBadges = TAB_OPTIONS.reduce((sum, o) => (o.value === tab ? sum : sum + (o.badge || 0)), 0);
  const current = TAB_OPTIONS.find((o) => o.value === tab) || TAB_OPTIONS[0];
  const CurrentIcon = current.icon;
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-900 grid place-items-center text-white">
          <ShieldCheck size={18} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Command Center</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Admin Panel</h1>
        </div>
      </div>

      <div className="mt-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="admin-tab-selector"
                className="w-full sm:w-auto min-w-[240px] inline-flex items-center justify-between gap-3 bg-white border border-slate-200 hover:border-[#00b4ff] rounded-full px-5 py-2.5 font-display font-semibold text-sm text-slate-900 shadow-sm"
                style={{ transition: "border-color 0.2s ease" }}
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <CurrentIcon size={16} className="text-[#00b4ff]" />
                  <span className="truncate">{current.label}</span>
                  {current.badge > 0 && (
                    <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono-plex font-bold grid place-items-center">
                      {current.badge > 9 ? "9+" : current.badge}
                    </span>
                  )}
                </span>
                <span className="inline-flex items-center gap-2 shrink-0">
                  {totalOtherBadges > 0 && (
                    <span data-testid="admin-tab-total-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono-plex font-bold grid place-items-center">
                      {totalOtherBadges > 9 ? "9+" : totalOtherBadges}
                    </span>
                  )}
                  <ChevronDown size={16} className="text-slate-400" />
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[260px] rounded-xl p-1">
              {TAB_OPTIONS.map((o) => {
                const Icon = o.icon;
                const active = o.value === tab;
                return (
                  <DropdownMenuItem
                    key={o.value}
                    data-testid={`admin-tab-option-${o.value}`}
                    onSelect={() => setTab(o.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer text-sm font-display font-semibold ${active ? "bg-[#00b4ff]/10 text-[#00b4ff]" : "text-slate-700"}`}
                  >
                    <Icon size={16} className={active ? "text-[#00b4ff]" : "text-slate-400"} />
                    <span className="flex-1 truncate">{o.label}</span>
                    {o.badge > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono-plex font-bold grid place-items-center">
                        {o.badge > 9 ? "9+" : o.badge}
                      </span>
                    )}
                    {active && <Check size={14} className="text-[#00b4ff]" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <TabsContent value="users" className="mt-6"><ApproveUsers /></TabsContent>
          <TabsContent value="messages" className="mt-6"><AdminDirectMessages /></TabsContent>
          <TabsContent value="broadcast" className="mt-6"><ManageAnnouncements /></TabsContent>
          <TabsContent value="ebooks" className="mt-6"><UploadEbook /></TabsContent>
          <TabsContent value="ebook-requests" className="mt-6"><ManageEbookRequests /></TabsContent>
          <TabsContent value="videos" className="mt-6"><UploadVideo /></TabsContent>
          <TabsContent value="video-requests" className="mt-6"><ManageVideoRequests /></TabsContent>
          <TabsContent value="signals" className="mt-6"><ManageSignals /></TabsContent>
          <TabsContent value="bookings" className="mt-6"><ManageBookings /></TabsContent>
          <TabsContent value="master-requests" className="mt-6"><ManageMasterRequests /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
